import React, { useState, useEffect, useCallback } from 'react';
import gmbApi from '../services/gmb';

// Vue détaillée + édition d'une fiche Google (Phase 1).
// Lecture : tout (live Google). Édition : horaires, horaires exceptionnels,
// description, téléphone(s), site web. Le reste est affiché en lecture seule.

const DAYS = [
  ['MONDAY', 'Lundi'], ['TUESDAY', 'Mardi'], ['WEDNESDAY', 'Mercredi'],
  ['THURSDAY', 'Jeudi'], ['FRIDAY', 'Vendredi'], ['SATURDAY', 'Samedi'], ['SUNDAY', 'Dimanche'],
];

function gTimeToStr(t) {
  if (!t) return '';
  return `${String(t.hours || 0).padStart(2, '0')}:${String(t.minutes || 0).padStart(2, '0')}`;
}
function strToGTime(s) {
  if (!s) return null;
  const [h, m] = s.split(':');
  return { hours: Number(h) || 0, minutes: Number(m) || 0 };
}
function periodsToModel(regularHours) {
  const model = {};
  DAYS.forEach(([d]) => { model[d] = []; });
  (regularHours?.periods || []).forEach((p) => {
    if (!p.openDay || !model[p.openDay]) return;
    model[p.openDay].push({ open: gTimeToStr(p.openTime) || '00:00', close: gTimeToStr(p.closeTime) || '00:00' });
  });
  return model;
}
function modelToPeriods(model) {
  const periods = [];
  DAYS.forEach(([d]) => {
    (model[d] || []).forEach((r) => {
      if (!r.open || !r.close) return;
      periods.push({ openDay: d, openTime: strToGTime(r.open), closeDay: d, closeTime: strToGTime(r.close) });
    });
  });
  return { periods };
}
function gDateToStr(dt) {
  if (!dt || !dt.year) return '';
  return `${dt.year}-${String(dt.month).padStart(2, '0')}-${String(dt.day).padStart(2, '0')}`;
}
function strToGDate(s) {
  if (!s) return null;
  const [y, m, d] = s.split('-');
  return { year: Number(y), month: Number(m), day: Number(d) };
}
function specialToModel(specialHours) {
  return (specialHours?.specialHourPeriods || []).map((p) => ({
    date: gDateToStr(p.startDate),
    closed: !!p.closed,
    open: gTimeToStr(p.openTime) || '09:00',
    close: gTimeToStr(p.closeTime) || '18:00',
  }));
}
function specialToG(list) {
  return {
    specialHourPeriods: list.filter((e) => e.date).map((e) => {
      const date = strToGDate(e.date);
      const base = { startDate: date, endDate: date };
      return e.closed ? { ...base, closed: true } : { ...base, openTime: strToGTime(e.open), closeTime: strToGTime(e.close) };
    }),
  };
}
function fmtAddress(a) {
  if (!a) return '—';
  return [...(a.addressLines || []), a.postalCode, a.locality, a.administrativeArea].filter(Boolean).join(', ');
}

export default function GmbLocationDetail({ location, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // États éditables
  const [description, setDescription] = useState('');
  const [website, setWebsite] = useState('');
  const [primaryPhone, setPrimaryPhone] = useState('');
  const [additionalPhones, setAdditionalPhones] = useState([]);
  const [hours, setHours] = useState({});
  const [special, setSpecial] = useState([]);
  const [dirty, setDirty] = useState({});

  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  const initFromDetail = useCallback((d) => {
    setDescription(d.profile?.description || '');
    setWebsite(d.websiteUri || '');
    setPrimaryPhone(d.phoneNumbers?.primaryPhone || '');
    setAdditionalPhones(d.phoneNumbers?.additionalPhones || []);
    setHours(periodsToModel(d.regularHours));
    setSpecial(specialToModel(d.specialHours));
    setDirty({});
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const { data } = await gmbApi.getLocationDetails(location.id);
      setData(data);
      initFromDetail(data.detail || {});
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur de chargement de la fiche Google.');
    } finally { setLoading(false); }
  }, [location.id, initFromDetail]);

  useEffect(() => { load(); }, [load]);

  const mark = (k) => setDirty((p) => ({ ...p, [k]: true }));

  const handleSave = async () => {
    const updates = {};
    const masks = [];
    if (dirty.desc)    { updates.profile = { description }; masks.push('profile.description'); }
    if (dirty.web)     { updates.websiteUri = website; masks.push('websiteUri'); }
    if (dirty.phone)   { updates.phoneNumbers = { primaryPhone, additionalPhones: additionalPhones.filter(Boolean) }; masks.push('phoneNumbers'); }
    if (dirty.hours)   { updates.regularHours = modelToPeriods(hours); masks.push('regularHours'); }
    if (dirty.special) { updates.specialHours = specialToG(special); masks.push('specialHours'); }

    if (masks.length === 0) { setSaveMsg('Aucune modification.'); return; }
    if (!window.confirm('Publier ces modifications sur la fiche Google PUBLIQUE ? Le changement sera visible en ligne après traitement par Google.')) return;

    setSaving(true); setSaveMsg(''); setError('');
    try {
      const { data: res } = await gmbApi.updateLocationDetails(location.id, { updates, updateMask: masks.join(',') });
      setData((prev) => ({ ...prev, detail: res.detail }));
      initFromDetail(res.detail || {});
      setSaveMsg('✅ Fiche mise à jour sur Google.');
    } catch (e) {
      setError(e.response?.data?.error || 'Échec de la mise à jour Google.');
    } finally { setSaving(false); }
  };

  const d = data?.detail || {};
  const dirtyCount = Object.values(dirty).filter(Boolean).length;

  return (
    <div className="gmb-drawer-overlay" onClick={onClose}>
      <div className="gmb-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="gmb-drawer-head">
          <div>
            <h2>{location.title || d.title || '(sans titre)'}</h2>
            <div className="gmb-loc-addr">{fmtAddress(d.storefrontAddress) || location.address}</div>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="gmb-drawer-body">
          {loading && <div className="gmb-empty">Chargement de la fiche Google…</div>}
          {error && <div className="status-error gmb-error">{error}</div>}

          {!loading && !error && (
            <>
              {data?.maps_url && (
                <a className="btn-secondary gmb-detail-link" href={data.maps_url} target="_blank" rel="noreferrer">
                  🔗 Voir la fiche en ligne sur Google ↗
                </a>
              )}

              {/* ── Lecture seule ── */}
              <div className="gmb-detail-section">
                <h3>Informations (lecture seule)</h3>
                <div className="gmb-kv">
                  <div><span>Catégorie principale</span>{d.categories?.primaryCategory?.displayName || '—'}</div>
                  <div><span>Catégories secondaires</span>{(d.categories?.additionalCategories || []).map((c) => c.displayName).join(', ') || '—'}</div>
                  <div><span>Statut</span>{d.openInfo?.status || '—'}</div>
                  <div><span>Code magasin</span>{d.storeCode || '—'}</div>
                </div>
              </div>

              {/* ── Éditable ── */}
              <div className="gmb-detail-section">
                <h3>Coordonnées</h3>
                <div className="form-group full">
                  <label>Téléphone principal</label>
                  <input className="page-search gmb-detail-input" value={primaryPhone}
                    onChange={(e) => { setPrimaryPhone(e.target.value); mark('phone'); }} placeholder="+590 590 12 34 56" />
                </div>
                {additionalPhones.map((p, i) => (
                  <div className="form-group full gmb-detail-phone-row" key={i}>
                    <label>Téléphone secondaire {i + 1}</label>
                    <div className="gmb-detail-inline">
                      <input className="page-search gmb-detail-input" value={p}
                        onChange={(e) => { setAdditionalPhones((arr) => arr.map((x, j) => (j === i ? e.target.value : x))); mark('phone'); }} />
                      <button className="btn-icon delete" title="Retirer"
                        onClick={() => { setAdditionalPhones((arr) => arr.filter((_, j) => j !== i)); mark('phone'); }}>🗑️</button>
                    </div>
                  </div>
                ))}
                <button className="btn-secondary gmb-ai-btn" onClick={() => { setAdditionalPhones((a) => [...a, '']); mark('phone'); }}>＋ Ajouter un téléphone</button>

                <div className="form-group full" style={{ marginTop: 12 }}>
                  <label>Site web</label>
                  <input className="page-search gmb-detail-input" value={website}
                    onChange={(e) => { setWebsite(e.target.value); mark('web'); }} placeholder="https://…" />
                </div>
              </div>

              <div className="gmb-detail-section">
                <h3>Description</h3>
                <textarea className="gmb-reply-textarea" rows={5} value={description}
                  onChange={(e) => { setDescription(e.target.value); mark('desc'); }}
                  placeholder="Présentation de l'établissement (max ~750 caractères)…" />
              </div>

              <div className="gmb-detail-section">
                <h3>Horaires réguliers</h3>
                {DAYS.map(([day, label]) => (
                  <div className="gmb-hours-row" key={day}>
                    <span className="gmb-hours-day">{label}</span>
                    <div className="gmb-hours-ranges">
                      {(hours[day] || []).length === 0 && <span className="gmb-hours-closed">Fermé</span>}
                      {(hours[day] || []).map((r, i) => (
                        <span className="gmb-hours-range" key={i}>
                          <input type="time" value={r.open}
                            onChange={(e) => { setHours((h) => ({ ...h, [day]: h[day].map((x, j) => (j === i ? { ...x, open: e.target.value } : x)) })); mark('hours'); }} />
                          <span>–</span>
                          <input type="time" value={r.close}
                            onChange={(e) => { setHours((h) => ({ ...h, [day]: h[day].map((x, j) => (j === i ? { ...x, close: e.target.value } : x)) })); mark('hours'); }} />
                          <button className="btn-icon delete" title="Retirer"
                            onClick={() => { setHours((h) => ({ ...h, [day]: h[day].filter((_, j) => j !== i) })); mark('hours'); }}>✕</button>
                        </span>
                      ))}
                      <button className="gmb-hours-add"
                        onClick={() => { setHours((h) => ({ ...h, [day]: [...(h[day] || []), { open: '09:00', close: '18:00' }] })); mark('hours'); }}>＋ plage</button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="gmb-detail-section">
                <h3>Horaires exceptionnels (jours fériés, fermetures…)</h3>
                {special.map((e, i) => (
                  <div className="gmb-special-row" key={i}>
                    <input type="date" value={e.date}
                      onChange={(ev) => { setSpecial((s) => s.map((x, j) => (j === i ? { ...x, date: ev.target.value } : x))); mark('special'); }} />
                    <label className="gmb-switch">
                      <input type="checkbox" checked={e.closed}
                        onChange={(ev) => { setSpecial((s) => s.map((x, j) => (j === i ? { ...x, closed: ev.target.checked } : x))); mark('special'); }} />
                      Fermé
                    </label>
                    {!e.closed && (
                      <span className="gmb-hours-range">
                        <input type="time" value={e.open}
                          onChange={(ev) => { setSpecial((s) => s.map((x, j) => (j === i ? { ...x, open: ev.target.value } : x))); mark('special'); }} />
                        <span>–</span>
                        <input type="time" value={e.close}
                          onChange={(ev) => { setSpecial((s) => s.map((x, j) => (j === i ? { ...x, close: ev.target.value } : x))); mark('special'); }} />
                      </span>
                    )}
                    <button className="btn-icon delete" title="Retirer"
                      onClick={() => { setSpecial((s) => s.filter((_, j) => j !== i)); mark('special'); }}>🗑️</button>
                  </div>
                ))}
                <button className="btn-secondary gmb-ai-btn" onClick={() => { setSpecial((s) => [...s, { date: '', closed: false, open: '09:00', close: '18:00' }]); mark('special'); }}>＋ Ajouter une date</button>
              </div>

              {/* ── Photos (lecture seule) ── */}
              <div className="gmb-detail-section">
                <h3>Photos {data?.media?.length ? `(${data.media.length})` : ''}</h3>
                {(!data?.media || data.media.length === 0) ? (
                  <span className="gmb-muted">Aucune photo (ou ajout en Phase 2).</span>
                ) : (
                  <div className="gmb-photo-grid">
                    {data.media.slice(0, 24).map((m) => (
                      <a key={m.name} href={m.googleUrl || m.thumbnailUrl} target="_blank" rel="noreferrer" className="gmb-photo">
                        <img src={m.thumbnailUrl || m.googleUrl} alt="" loading="lazy" />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {!loading && !error && (
          <div className="gmb-drawer-foot">
            {saveMsg && <span className="gmb-detail-msg">{saveMsg}</span>}
            <span className="gmb-muted">{dirtyCount > 0 ? `${dirtyCount} modification(s) en attente` : 'Aucune modification'}</span>
            <button className="btn-secondary" onClick={onClose}>Fermer</button>
            <button className="btn-primary" onClick={handleSave} disabled={saving || dirtyCount === 0}>
              {saving ? 'Publication…' : 'Publier sur Google'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
